import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { AlertTriangle, Mail, Plus, RefreshCw, Trash2 } from 'react-feather';
import {
  emailAliasesApi,
  type EmailAlias,
  type EmailAliasRecovery,
} from '../../../../services/api/emailAliases';
import { describeAliasRecovery } from './describeAliasRecovery';
import { getApiErrorMessage } from '../../../../services/api/client';

/**
 * How long to wait before reading the list back after a request that never
 * answered. The API commits the alias row before the slow recovery starts, so
 * the row is usually there already, but the commit can land just after the
 * client gives up.
 */
const SETTLE_BEFORE_RECHECK_MS = 1500;

/**
 * Statuses that mean "nobody answered for the API", not "the API refused".
 *
 * A proxy answers with a status of its own while the API is still working. The
 * load balancer returns 504 when it closes an idle connection, and 502 or 503
 * when a pod restarts mid request. Treating those as a refusal told the admin
 * their save had failed while the alias was already in the database, and they
 * retyped it and started the whole re-import again.
 */
const GATEWAY_STATUSES = [502, 503, 504];

/**
 * The interceptor in services/api/client.ts turns HTTP 429 into a plain Error
 * with no status, after showing its own toast. Without this test a rate
 * limited request falls into the "no answer" branch and the admin reads that
 * the address may have been saved, for a request that never reached the
 * handler. The message text is the only signal the interceptor leaves behind.
 */
const RATE_LIMIT_MESSAGE = 'Too many requests';

type LoadResult =
  | { ok: true; aliases: EmailAlias[] }
  | { ok: false; message: string };

interface ParticipantEmailAliasesProps {
  cohortId: string;
  participantId: string;
  /**
   * The address the participant signs in with, shown as the anchor.
   *
   * This must be the saved address, never an unsaved edit form draft. The row
   * is badged "Signs in", and a draft shown there contradicts what the API
   * says when the admin adds the real one.
   */
  primaryEmail: string | null;
}

/**
 * MDF-127 - the screen that fixes a submission nobody could link.
 *
 * The orphan alert email tells a Cohort Manager to recover a submission and
 * now names these steps. This is where those steps land, so the wording here
 * has to match the email.
 */

export function ParticipantEmailAliases({
  cohortId,
  participantId,
  primaryEmail,
}: ParticipantEmailAliasesProps) {
  const [aliases, setAliases] = useState<EmailAlias[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  // What the last recovery did. The toast alone is not enough: it holds the
  // only record of what was written and Mantine closes it after 4 seconds.
  const [lastReport, setLastReport] = useState<{
    message: string;
    color: 'green' | 'yellow';
  } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // The recheck below waits, so the modal can close before it resumes.
  const mounted = useRef(true);
  // The input stays enabled so focus is never dropped, which means Enter can
  // still fire while a save is in flight. State updates are not synchronous,
  // so the guard has to be a ref.
  const inFlight = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async (): Promise<LoadResult> => {
    try {
      return { ok: true, aliases: await emailAliasesApi.list(cohortId, participantId) };
    } catch (err) {
      // Returning an empty list here would be a lie. A participant with no
      // extra addresses and a participant whose list failed to load look
      // identical on screen, and only one of them is safe to act on.
      return { ok: false, message: getApiErrorMessage(err, 'Failed to load email addresses') };
    }
  }, [cohortId, participantId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .then(result => {
        if (cancelled) return;
        if (result.ok) {
          setAliases(result.aliases);
          setLoadError(null);
        } else {
          setLoadError(result.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load, reloadKey]);

  /** Put the focus back where the admin left it, so the error is announced. */
  const refocusInput = () => {
    if (mounted.current) inputRef.current?.focus();
  };

  const reportRecovery = (email: string, recovery: EmailAliasRecovery) => {
    const report = describeAliasRecovery(recovery);
    setLastReport(report);
    notifications.show({
      title: `${email} added`,
      message: report.message,
      color: report.color,
      // Never auto close this one. It is the only account of what the save
      // wrote, it can run to four sentences, and one of those sentences can be
      // an instruction to act.
      autoClose: false,
    });
  };

  const handleAdd = async () => {
    const email = newEmail.trim();
    if (!email || inFlight.current) return;

    inFlight.current = true;
    setSaving(true);
    setError(null);
    try {
      const result = await emailAliasesApi.add(cohortId, participantId, email);
      reportRecovery(result.alias.email, result.recovery);

      // Keep the address in the box when the message asks for another pass.
      // Clearing it made the admin retype an address to follow an instruction
      // they had just been given. A repeat save is supported: the API returns
      // the existing alias and runs recovery again rather than a conflict.
      const askedToSaveAgain =
        result.recovery.replay_capped ||
        result.recovery.failures.some(failure => failure.retryable);
      if (!askedToSaveAgain) setNewEmail('');

      const refreshed = await load();
      if (!mounted.current) return;
      if (refreshed.ok) {
        setAliases(refreshed.aliases);
        setLoadError(null);
      } else {
        // The save worked. Only the read back failed, so show the row the POST
        // returned instead of a list that looks unchanged. An admin who cannot
        // see the address retypes it and starts the whole import again.
        setAliases(current =>
          current.some(a => a.id === result.alias.id) ? current : [...current, result.alias]
        );
        setLoadError(refreshed.message);
      }
    } catch (err) {
      const status = (err as { status?: number }).status;
      const message = (err as { message?: string }).message ?? '';

      if (message.startsWith(RATE_LIMIT_MESSAGE)) {
        // The request never reached the handler, so nothing was saved and
        // there is nothing to re-read.
        setError('Too many requests just now. Wait a moment and add it again.');
        refocusInput();
        return;
      }

      if (status && !GATEWAY_STATUSES.includes(status)) {
        // The API itself answered and refused. Its messages are written for a
        // person to read, so show one as is rather than keeping a second copy
        // of the same sentence here.
        setError(getApiErrorMessage(err, 'Could not add that address'));
        refocusInput();
        return;
      }

      // No answer from the API: a client timeout, a dropped connection, or a
      // proxy answering on its behalf. The address may still be saved, because
      // the API commits the row before the recovery starts and the recovery is
      // the slow part. Reporting a plain failure would tell the admin their
      // save was lost when it was not, and they would retry and start the
      // whole import again. Raw axios text such as "timeout of 110000ms
      // exceeded" is no use to them either.
      //
      // Wait before reading back. The commit can land just after the client
      // gives up, and an immediate read returns the list without it.
      await new Promise(resolve => setTimeout(resolve, SETTLE_BEFORE_RECHECK_MS));
      if (!mounted.current) return;

      const refreshed = await load();
      if (!mounted.current) return;
      if (refreshed.ok) {
        setAliases(refreshed.aliases);
        setLoadError(null);
      } else {
        setLoadError(refreshed.message);
      }

      const saved =
        refreshed.ok && refreshed.aliases.some(a => a.email.toLowerCase() === email.toLowerCase());

      if (saved) {
        setNewEmail('');
        notifications.show({
          title: `${email} is saved`,
          message:
            'The recovery did not report back in time. It may still be running. Open this participant again in a few minutes, or save the address again to run it once more.',
          color: 'yellow',
          autoClose: false,
        });
      } else {
        setError(
          'That did not report back in time. The address may still have been saved. Close this and open the participant again to check.'
        );
        refocusInput();
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const handleRemove = (alias: EmailAlias) => {
    if (removingId) return;
    modals.openConfirmModal({
      title: 'Remove this email address?',
      children: (
        <Text size="sm">
          Submissions already linked through {alias.email} stay linked. New submissions from that
          address will stop reaching this person.
        </Text>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setRemovingId(alias.id);
        try {
          await emailAliasesApi.remove(cohortId, participantId, alias.id);
          if (!mounted.current) return;
          setAliases(current => current.filter(a => a.id !== alias.id));
          notifications.show({
            title: 'Address removed',
            message: alias.email,
            color: 'green',
          });
        } catch (err) {
          if (!mounted.current) return;
          // A second delete of the same row answers 404. The address is gone
          // either way, so reporting a failure for a removal that worked would
          // be wrong.
          if ((err as { status?: number }).status === 404) {
            setAliases(current => current.filter(a => a.id !== alias.id));
            return;
          }
          notifications.show({
            title: 'Could not remove that address',
            message: getApiErrorMessage(err, 'Failed to remove the address'),
            color: 'red',
          });
        } finally {
          if (mounted.current) setRemovingId(null);
        }
      },
    });
  };

  const shadowedExplanation =
    'Somebody now signs in with this address, and a sign in address always wins. This one no longer matches any submission.';

  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        A participant can fill in a form with a work address we do not hold, and the response
        reaches nobody. Add that address here and the past responses are recovered too.
      </Text>

      <Group gap="xs">
        <Mail size={14} />
        <Text size="sm" fw={500}>
          {primaryEmail || 'No sign in address'}
        </Text>
        <Badge size="xs" variant="light" color="gray">
          Signs in
        </Badge>
      </Group>

      {loading && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Loading addresses
          </Text>
        </Group>
      )}

      {!loading && loadError && (
        <Group gap="xs" role="alert">
          <Text size="sm" c="red">
            Could not load the other addresses. {loadError}
          </Text>
          <Button
            size="compact-xs"
            variant="subtle"
            leftSection={<RefreshCw size={12} />}
            onClick={() => setReloadKey(key => key + 1)}
          >
            Retry
          </Button>
        </Group>
      )}

      {!loading && !loadError && aliases.length === 0 && (
        <Text size="sm" c="dimmed">
          No other addresses yet.
        </Text>
      )}

      {!loading &&
        aliases.map(alias => (
          <Group key={alias.id} gap="xs" wrap="nowrap">
            <Mail size={14} />
            <Text size="sm" fw={500} style={{ flex: 1 }}>
              {alias.email}
            </Text>
            {alias.shadowed && (
              // tabIndex and aria-label both matter. A Mantine Badge renders a
              // plain div, so without them the tooltip opens on hover only and
              // a keyboard user reads "Not matching" with no reason given, on
              // the one row that silently matches nothing.
              <Tooltip multiline w={260} label={shadowedExplanation}>
                <Badge
                  size="xs"
                  variant="light"
                  color="yellow"
                  tabIndex={0}
                  aria-label={`Not matching. ${shadowedExplanation}`}
                  leftSection={<AlertTriangle size={10} />}
                >
                  Not matching
                </Badge>
              </Tooltip>
            )}
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              aria-label={`Remove ${alias.email}`}
              loading={removingId === alias.id}
              disabled={removingId !== null && removingId !== alias.id}
              onClick={() => handleRemove(alias)}
            >
              <Trash2 size={14} />
            </ActionIcon>
          </Group>
        ))}

      <Group gap="xs" align="flex-start">
        <TextInput
          ref={inputRef}
          size="xs"
          type="email"
          aria-label="Add another work address"
          placeholder="Add another work address"
          style={{ flex: 1 }}
          value={newEmail}
          // The input stays enabled while saving. Disabling it made the browser
          // drop focus, and the error below then appeared on an element nobody
          // was on, so a screen reader never announced it. handleAdd guards the
          // double submit instead.
          error={!!error}
          onChange={event => {
            setNewEmail(event.currentTarget.value);
            if (error) setError(null);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleAdd();
            }
          }}
        />
        <Button
          size="xs"
          variant="light"
          leftSection={<Plus size={14} />}
          loading={saving}
          disabled={newEmail.trim().length === 0}
          onClick={() => void handleAdd()}
        >
          Add
        </Button>
      </Group>

      {error && (
        // role="alert" rather than the TextInput's own error text. Mantine
        // renders that as plain markup in no live region, so it is silent.
        <Text size="xs" c="red" role="alert">
          {error}
        </Text>
      )}

      {lastReport && (
        <Box role="status">
          <Text size="xs" c={lastReport.color === 'yellow' ? 'yellow.8' : 'dimmed'}>
            {lastReport.message}
          </Text>
        </Box>
      )}
    </Stack>
  );
}

export default ParticipantEmailAliases;
